import { useState, useRef, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ShoppingCart, ArrowRight, Package } from "@phosphor-icons/react";
import { useCart } from "../../contexts/CartContext";

const fmt = (v, c = "EUR") =>
  new Intl.NumberFormat("pt-PT", { style: "currency", currency: c || "EUR" }).format(v || 0);

/**
 * Topbar mini-cart: a badge with the item count and a dropdown panel
 * preview listing the first 4 items with totals and quick CTAs.
 */
export default function CartBadge() {
  const { produtos, packs, itemCount, total, currency } = useCart();
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    const onClick = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    window.addEventListener("mousedown", onClick);
    return () => window.removeEventListener("mousedown", onClick);
  }, []);

  const items = [...produtos, ...packs].slice(0, 4);
  const extra = itemCount - items.length;

  return (
    <div ref={wrapRef} className="relative" data-testid="cart-badge">
      <button type="button" onClick={() => setOpen((o) => !o)}
        data-testid="cart-badge-btn"
        className="relative w-9 h-9 flex items-center justify-center rounded-lg hover:bg-[#F3F4F6] transition-colors"
        aria-label="Carrinho">
        <ShoppingCart size={20} className="text-[#0F1829]" weight={itemCount > 0 ? "fill" : "regular"} />
        {itemCount > 0 && (
          <span data-testid="cart-badge-count"
            className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-[#FF2B2B] text-white text-[10px] font-bold flex items-center justify-center leading-none">
            {itemCount > 99 ? "99+" : itemCount}
          </span>
        )}
      </button>

      {open && (
        <div className="fixed left-3 right-3 top-[70px] w-auto max-w-none z-50 sm:absolute sm:left-auto sm:right-0 sm:top-auto sm:mt-2 sm:w-[340px] sm:max-w-[340px] bg-white rounded-xl border border-[#E5E7EB] shadow-2xl overflow-hidden"
          data-testid="cart-badge-dropdown" style={{ fontFamily: "'Manrope', sans-serif" }}>
          <div className="p-4 border-b border-[#E5E7EB]">
            <h3 className="text-base font-black tracking-tighter text-[#0F1829] flex items-center gap-2" style={{ fontFamily: "'Fraunces', sans-serif" }}>
              <ShoppingCart size={18} weight="fill" className="text-[#002480]" />
              Carrinho
              <span className="text-xs font-medium text-[#6B7280] ml-auto">{itemCount} {itemCount === 1 ? "item" : "itens"}</span>
            </h3>
          </div>

          {itemCount === 0 ? (
            <div className="p-6 text-center">
              <Package size={32} className="mx-auto text-[#9CA3AF] mb-2" />
              <p className="text-sm text-[#6B7280]">O seu carrinho está vazio.</p>
            </div>
          ) : (
            <>
              <div className="max-h-[280px] overflow-y-auto">
                {items.map((it, idx) => {
                  const isPack = !!it.ID_Pack;
                  const id = isPack ? it.ID_Pack : it.ID_Produto;
                  return (
                    <div key={`${isPack ? "p" : "i"}-${id}-${idx}`}
                      className="p-3 flex items-center gap-3 border-b border-[#E5E7EB] last:border-b-0"
                      data-testid={`cart-badge-item-${id}`}>
                      <div className="w-9 h-9 shrink-0 rounded-lg bg-[#F3F4F6] flex items-center justify-center">
                        <Package size={14} className="text-[#6B7280]" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-xs font-medium text-[#0F1829] truncate">{it.Descricao || "Produto"}</p>
                        <p className="text-[11px] text-[#6B7280] mt-0.5">Qtd. {it.Quantidade ?? 1} · {fmt(Number(it.subtotal) || 0, currency)}</p>
                      </div>
                    </div>
                  );
                })}
                {extra > 0 && (
                  <div className="p-2 text-center text-[11px] text-[#6B7280] bg-[#F9FAFB]">
                    + {extra} {extra === 1 ? "item" : "itens"} no carrinho
                  </div>
                )}
              </div>

              <div className="p-4 bg-[#F9FAFB] border-t border-[#E5E7EB]">
                <div className="flex justify-between text-sm font-semibold text-[#0F1829] mb-3">
                  <span>Total</span>
                  <span data-testid="cart-badge-total">{fmt(total, currency)}</span>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <Link to="/my/carrinho" onClick={() => setOpen(false)}
                    data-testid="cart-badge-view"
                    className="text-center py-2 text-xs font-medium text-[#0F1829] bg-white border border-[#E5E7EB] rounded-md hover:bg-[#F3F4F6] transition-colors">
                    Ver carrinho
                  </Link>
                  <button type="button"
                    onClick={() => { setOpen(false); navigate("/my/checkout"); }}
                    data-testid="cart-badge-checkout"
                    className="py-2 text-xs font-semibold text-white bg-[#0F1829] rounded-md hover:bg-[#002480] transition-colors flex items-center justify-center gap-1">
                    Finalizar <ArrowRight size={12} />
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}
